import React from "react";
import { Link } from "react-router-dom";
import { useCompare } from "../context/CompareContext";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../utils/format";
import { X, ShoppingCart, GitCompare } from "lucide-react";
import { Star } from "lucide-react";
import toast from "react-hot-toast";

export default function ComparePage() {
  const { compareList, removeFromCompare, clearCompare } = useCompare();
  const { addToCart } = useCart();

  if (compareList.length === 0) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
        <GitCompare size={48} style={{ opacity: 0.2 }} />
        <h2 style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 700 }}>No products to compare</h2>
        <p style={{ color: "var(--text-muted)" }}>Add up to 3 products using the compare button on product pages.</p>
        <Link to="/shop" className="btn btn-primary">Browse Products</Link>
      </div>
    );
  }

  // Collect all spec keys across all products
  const allSpecs = Array.from(new Set(compareList.flatMap(p => Object.keys(p.specs || {}))));

  const rows = [
    { label: "Image",     render: p => p.images?.[0] ? <img src={p.images[0]} alt="" style={{ width: "100%", height: 140, objectFit: "contain" }} loading="lazy" /> : <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40 }}>📦</div> },
    { label: "Price",     render: p => <span style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800, color: "var(--brand)" }}>{formatPrice(p.price)}</span> },
    { label: "Category",  render: p => p.category },
    { label: "Brand",     render: p => p.brand || "—" },
    { label: "Rating",    render: p => p.rating > 0 ? (<div style={{ display: "flex", alignItems: "center", gap: 4 }}>{[1,2,3,4,5].map(s => <Star key={s} size={14} fill={s <= Math.round(p.rating) ? "#F59E0B" : "none"} color="#F59E0B" />)}<span style={{ fontSize: 13 }}>{p.rating} ({p.reviewCount || 0})</span></div>) : "No reviews" },
    { label: "Stock",     render: p => <span style={{ color: p.stock > 0 ? "var(--success)" : "var(--danger)", fontWeight: 500 }}>{p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}</span> },
    ...allSpecs.map(spec => ({ label: spec, render: p => p.specs?.[spec] || "—" })),
    { label: "Action",    render: p => (
      <button onClick={() => { addToCart({ id: p.id, name: p.name, price: p.price, image: p.images?.[0] || "", category: p.category }); toast.success("Added to cart"); }}
        className="btn btn-primary btn-sm" disabled={p.stock <= 0}>
        <ShoppingCart size={14} /> Add to Cart
      </button>
    )},
  ];

  return (
    <div style={{ background: "var(--surface)", minHeight: "100vh" }}>
      <div className="container section-sm">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 26, fontWeight: 700 }}>Compare Products</h1>
          <button onClick={clearCompare} className="btn btn-secondary btn-sm">Clear All</button>
        </div>

        <div className="card" style={{ overflow: "auto" }}>
          <table className="compare-table" style={{ width: "100%", minWidth: 500 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                <th style={{ padding: "16px 20px", width: 160, background: "var(--surface)" }}>Product</th>
                {compareList.map(p => (
                  <th key={p.id} style={{ padding: "16px 20px", fontWeight: 700, fontSize: 14, position: "relative" }}>
                    <button onClick={() => removeFromCompare(p.id)}
                      style={{ position: "absolute", top: 8, right: 8, border: "none", background: "transparent", cursor: "pointer", color: "var(--text-muted)" }}>
                      <X size={14} />
                    </button>
                    <Link to={`/product/${p.id}`} style={{ color: "var(--text)", display: "block", paddingRight: 20 }}>{p.name}</Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ label, render }, i) => (
                <tr key={label} style={{ background: i % 2 === 0 ? "#fff" : "var(--surface)" }}>
                  <th style={{ padding: "14px 20px", fontWeight: 600, fontSize: 13, color: "var(--text-muted)", textAlign: "left", whiteSpace: "nowrap" }}>{label}</th>
                  {compareList.map(p => (
                    <td key={p.id} style={{ padding: "14px 20px", fontSize: 14, verticalAlign: "middle" }}>
                      {render(p)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
